import { notify } from '@/lib/toast';
import { Dropdown } from 'primereact/dropdown';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MapPin, Plus, Building2, Warehouse, Package, UserCheck, UserX, CreditCard, ChevronDown, ChevronUp } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Button } from '@/components/ui/button';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormDialog } from '@/components/ui/form-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { SearchInput } from '@/components/ui/search-input';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { Switch } from '@/components/ui/switch';
import { isValidPhoneNumber } from '@/lib/pk-mobile';
import { invalidateLocationQueries } from '@/lib/locations-cache';

const NAME_MAX = 100;
const STOCK_PAGE_SIZE = 50;
const PAYMENT_METHODS = ['CASH', 'CARD', 'BANK_TRANSFER', 'MOBILE_WALLET', 'CREDIT'];

const LOCATION_TYPES = [
  { value: 'STORE', label: 'Store' },
  { value: 'WAREHOUSE', label: 'Warehouse' },
  { value: 'DISTRIBUTION_CENTER', label: 'Distribution Center' },
];

const TYPE_BADGES: Record<string, string> = {
  STORE: 'bg-info/10 text-info-text',
  WAREHOUSE: 'bg-primary/10 text-primary-text',
  DISTRIBUTION_CENTER: 'bg-warning/10 text-warning-text',
};

const TYPE_LABELS: Record<string, string> = {
  STORE: 'Store',
  WAREHOUSE: 'Warehouse',
  DISTRIBUTION_CENTER: 'Distribution Center',
};

type PaymentMethodRules = Record<string, { rate: number; name: string; overridesProductTax: boolean }>;

const defaultPmRules = (): PaymentMethodRules => ({
  CASH:          { rate: 15, name: 'ICT Sales Tax (Cash)',    overridesProductTax: true },
  CARD:          { rate: 5,  name: 'ICT Sales Tax (Digital)', overridesProductTax: true },
  MOBILE_WALLET: { rate: 5,  name: 'ICT Sales Tax (Digital)', overridesProductTax: true },
  BANK_TRANSFER: { rate: 5,  name: 'ICT Sales Tax (Digital)', overridesProductTax: true },
  CREDIT:        { rate: 5,  name: 'ICT Sales Tax (Digital)', overridesProductTax: true },
});

interface LocationRow {
  id: string;
  name: string;
  type: string;
  address?: string | null;
  city?: string | null;
  phone?: string | null;
  isDefault: boolean;
  isActive: boolean;
  paymentMethodTaxEnabled?: boolean | null;
  paymentMethodTaxRules?: PaymentMethodRules | null;
  atlEnabled?: boolean | null;
  defaultBuyerAtlStatus?: boolean | null;
  furtherTaxRate?: number | string | null;
  fbrSroScheduleNo?: string | null;
}

interface LocationStockRow {
  id: string;
  productId: string;
  quantity: number;
  product?: { name?: string; sku?: string | null } | null;
}

const emptyForm = {
  name: '', type: 'STORE', address: '', city: '', phone: '', isDefault: false,
  // Tax rules
  paymentMethodTaxEnabled: false,
  paymentMethodTaxRules: null as PaymentMethodRules | null,
  atlEnabled: false,
  defaultBuyerAtlStatus: false,
  furtherTaxRate: 3 as number | '',
  fbrSroScheduleNo: '',
};

type FormErrors = { name?: string; phone?: string; furtherTaxRate?: string };

export function LocationsPage() {
  const { t } = useTranslation('settings');
  const statusOptions = [
    { label: t('locStatusActive'), value: 'active' },
    { label: t('locStatusInactive'), value: 'inactive' },
    { label: t('locStatusAll'), value: 'all' },
  ];
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [stockPage, setStockPage] = useState(1);
  const [errors, setErrors] = useState<FormErrors>({});
  const [page, setPage] = useState(1);
  const limit = 20;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);
  const [taxSectionOpen, setTaxSectionOpen] = useState(false);
  const [stockLocationId, setStockLocationId] = useState<string | null>(null);
  const [deactivateConfirm, setDeactivateConfirm] = useState<{ open: boolean; id: string; name: string }>({ open: false, id: '', name: '' });

  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('locations:create') || hasPermission('locations:*');
  const canUpdate = hasPermission('locations:update') || hasPermission('locations:*');
  const canDelete = hasPermission('locations:delete') || hasPermission('locations:*');
  const canViewFbr = hasPermission('fbr:*') || hasPermission('fbr:read') || hasPermission('tax-rates:*');

  const { data, isLoading } = useQuery({
    queryKey: ['locations', search, typeFilter, statusFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search) params.set('search', search);
      if (typeFilter) params.set('type', typeFilter);
      if (statusFilter !== 'active') params.set('status', statusFilter);
      const res = await api.get(`/locations?${params}`);
      return res.data;
    },
  });

  const { data: stockData } = useQuery({
    queryKey: ['location-stock', stockLocationId, stockPage],
    queryFn: async () => {
      if (!stockLocationId) return null;
      const res = await api.get(`/locations/${stockLocationId}/stock?page=${stockPage}&limit=${STOCK_PAGE_SIZE}`);
      return res.data;
    },
    enabled: !!stockLocationId,
  });

  const locations: LocationRow[] = data?.data ?? data ?? [];
  const locTotal: number = data?.meta?.total ?? data?.total ?? locations.length;

  const saveMutation = useMutation({
    mutationFn: (body: ReturnType<typeof buildPayload>) =>
      editId ? api.patch(`/locations/${editId}`, body) : api.post('/locations', body),
    onSuccess: () => {
      toast.success(editId ? 'Location updated' : 'Location created');
      invalidateLocationQueries(queryClient);
      setDialogOpen(false);
      setEditId(null);
      setErrors({});
      setForm(emptyForm);
    },
    onError: (err) => notify.error(err, 'Failed to save location'),
  });

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/locations/${id}/default`),
    onSuccess: () => {
      toast.success('Default location updated');
      invalidateLocationQueries(queryClient);
    },
    onError: (err) => notify.error(err, 'Failed to set default'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/locations/${id}`),
    onSuccess: () => {
      toast.success('Location deactivated');
      invalidateLocationQueries(queryClient);
    },
    onError: (err) => notify.error(err, 'Failed to deactivate location'),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/locations/${id}`, { isActive: true }),
    onSuccess: () => {
      toast.success(t('locReactivated'));
      invalidateLocationQueries(queryClient);
    },
    onError: (err) => notify.error(err, t('locReactivateFailed')),
  });

  function openStock(id: string) {
    setStockPage(1);
    setStockLocationId(id);
  }

  /** POS-I317/I319/I320/I325: the same rules as the API, shown under the field before anything is sent. */
  function validateForm(): FormErrors {
    const e: FormErrors = {};
    const name = form.name.trim();
    if (!name) e.name = t('locNameRequired');
    else if (name.length > NAME_MAX) e.name = t('locNameTooLong', { max: NAME_MAX });
    else if (/[<>]/.test(name)) e.name = t('locNameChars');
    if (form.phone.trim() && !isValidPhoneNumber(form.phone.trim())) e.phone = t('locPhoneInvalid');
    if (form.atlEnabled && canViewFbr) {
      const r = form.furtherTaxRate;
      if (r === '' || Number.isNaN(Number(r)) || Number(r) < 0 || Number(r) > 100) e.furtherTaxRate = t('locFurtherTaxInvalid');
    }
    return e;
  }

  function openCreate() {
    setEditId(null);
    setErrors({});
    setForm(emptyForm);
    setTaxSectionOpen(false);
    setDialogOpen(true);
  }

  function openEdit(loc: LocationRow) {
    setEditId(loc.id);
    setErrors({});
    setForm({
      name: loc.name,
      type: loc.type,
      address: loc.address ?? '',
      city: loc.city ?? '',
      phone: loc.phone ?? '',
      isDefault: loc.isDefault,
      paymentMethodTaxEnabled: loc.paymentMethodTaxEnabled ?? false,
      paymentMethodTaxRules: loc.paymentMethodTaxRules ?? null,
      atlEnabled: loc.atlEnabled ?? false,
      defaultBuyerAtlStatus: loc.defaultBuyerAtlStatus ?? false,
      furtherTaxRate: Number(loc.furtherTaxRate ?? 3),
      fbrSroScheduleNo: loc.fbrSroScheduleNo ?? '',
    });
    setTaxSectionOpen(loc.paymentMethodTaxEnabled || loc.atlEnabled || !!loc.fbrSroScheduleNo);
    setDialogOpen(true);
  }

  function updatePmRule(method: string, field: string, value: string) {
    const current = form.paymentMethodTaxRules ?? defaultPmRules();
    setForm((prev) => ({
      ...prev,
      paymentMethodTaxRules: {
        ...current,
        [method]: { ...current[method], [field]: field === 'rate' ? Number(value) : value },
      },
    }));
  }

  function handleEnablePaymentTax(enabled: boolean) {
    setForm((prev) => ({
      ...prev,
      paymentMethodTaxEnabled: enabled,
      paymentMethodTaxRules: enabled ? (prev.paymentMethodTaxRules ?? defaultPmRules()) : prev.paymentMethodTaxRules,
      // Auto-fill SRO with ICT ordinance reference when enabling pm-tax (if not already set)
      fbrSroScheduleNo: enabled && !prev.fbrSroScheduleNo
        ? 'ICT Tax on Services Ordinance 2001'
        : prev.fbrSroScheduleNo,
    }));
    if (enabled) setTaxSectionOpen(true);
  }

  function buildPayload() {
    return {
      name: form.name.trim(),
      type: form.type,
      address: form.address || undefined,
      city: form.city || undefined,
      phone: form.phone.trim() || undefined,
      // POS-I323: only a Store can be the default; for other types the flag is left as it is
      isDefault: form.type === 'STORE' ? form.isDefault : undefined,
      paymentMethodTaxEnabled: form.paymentMethodTaxEnabled,
      paymentMethodTaxRules: form.paymentMethodTaxEnabled ? (form.paymentMethodTaxRules ?? defaultPmRules()) : null,
      atlEnabled: form.atlEnabled,
      defaultBuyerAtlStatus: form.defaultBuyerAtlStatus,
      furtherTaxRate: form.furtherTaxRate === '' ? undefined : form.furtherTaxRate,
      fbrSroScheduleNo: form.fbrSroScheduleNo || undefined,
    };
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Locations & Warehouses"
        subtitle="Manage your business locations and warehouses"
        actions={canCreate && (
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 me-2" /> Add Location
          </Button>
        )}
      />

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <SearchInput
          value={search}
          onChange={(v) => { setSearch(v); setPage(1); }}
          placeholder="Search locations..."
          className="flex-1 min-w-[200px]"
        />
        <Dropdown
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.value); setPage(1); }}
          options={[{ label: 'All Types', value: '' }, ...LOCATION_TYPES.map((t) => ({ label: t.label, value: t.value }))]}
          optionLabel="label"
          optionValue="value"
          placeholder="All Types"
          aria-label={t('locTypeAria')}
        />
        <Dropdown
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.value); setPage(1); }}
          options={statusOptions}
          optionLabel="label"
          optionValue="value"
          aria-label={t('locStatusAria')}
        />
      </div>

      {/* Locations Grid */}
      {isLoading ? (
        <SkeletonTable rows={4} cols={3} />
      ) : locations.length === 0 ? (
        <EmptyState
          icon={MapPin}
          title="No locations found"
          subtitle="Create your first location to manage stock by store or warehouse."
          action={canCreate ? { label: 'Add Location', onClick: openCreate } : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {locations.map((loc: LocationRow) => (
            <Card key={loc.id} className={`relative ${!loc.isActive ? 'opacity-60' : ''}`}>
              {loc.isDefault && (
                <span className="absolute top-3 end-3 text-xs bg-success/10 text-success-text px-2 py-0.5 rounded-full font-medium">Default</span>
              )}
              <CardHeader className="pb-2">
                <div className={`flex items-center gap-2 min-w-0 ${loc.isDefault ? 'pe-16' : ''}`}>
                  {loc.type === 'STORE' ? (
                    <Building2 className="h-5 w-5 text-info-text" />
                  ) : (
                    <Warehouse className="h-5 w-5 text-primary-text" />
                  )}
                  <CardTitle className="text-base min-w-0 [overflow-wrap:anywhere]">{loc.name}</CardTitle>
                </div>
                <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium w-fit ${TYPE_BADGES[loc.type] ?? ''}`}>
                  {TYPE_LABELS[loc.type] ?? loc.type}
                </span>
                {!loc.isActive && (
                  <span className="ms-2 inline-block text-xs px-2 py-0.5 rounded-full font-medium bg-muted text-muted-foreground">{t('locStatusInactive')}</span>
                )}
              </CardHeader>
              <CardContent className="space-y-2">
                {loc.city && <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">{loc.city}</p>}
                {loc.address && <p className="text-xs text-muted-foreground [overflow-wrap:anywhere]">{loc.address}</p>}
                {loc.phone && <p className="text-xs text-muted-foreground">📞 {loc.phone}</p>}

                {/* Tax rule badges */}
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {loc.paymentMethodTaxEnabled && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-info/10 px-2 py-0.5 text-xs font-medium text-info-text">
                      <CreditCard className="h-2.5 w-2.5" /> Method Tax ON
                    </span>
                  )}
                  {canViewFbr && loc.atlEnabled && (
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${loc.defaultBuyerAtlStatus ? 'bg-success/10 text-success-text' : 'bg-warning/10 text-warning-text'}`}>
                      {loc.defaultBuyerAtlStatus ? <UserCheck className="h-2.5 w-2.5" /> : <UserX className="h-2.5 w-2.5" />}
                      ATL {loc.defaultBuyerAtlStatus ? 'default' : `+${loc.furtherTaxRate ?? 3}%`}
                    </span>
                  )}
                  {loc.fbrSroScheduleNo && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary-text">
                      SRO: {loc.fbrSroScheduleNo}
                    </span>
                  )}
                </div>

                <div className="flex gap-2 pt-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1"
                    onClick={() => openStock(loc.id)}
                  >
                    <Package className="h-3.5 w-3.5" /> Stock
                  </Button>
                  {canUpdate && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => openEdit(loc)}>Edit</Button>
                      {!loc.isActive && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={reactivateMutation.isPending}
                          onClick={() => reactivateMutation.mutate(loc.id)}
                        >
                          {t('locReactivate')}
                        </Button>
                      )}
                      {loc.isActive && !loc.isDefault && loc.type === 'STORE' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDefaultMutation.mutate(loc.id)}
                        >
                          Set Default
                        </Button>
                      )}
                    </>
                  )}
                  {canDelete && loc.isActive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive-text hover:text-destructive-text"
                      onClick={() => setDeactivateConfirm({ open: true, id: loc.id, name: loc.name })}
                    >
                      Deactivate
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {locTotal > limit && (
        <div className="border-t px-4 py-3">
          <PaginationBar page={page} pageSize={limit} total={locTotal} onPageChange={setPage} />
        </div>
      )}

      {/* Create / Edit Dialog */}
      <FormDialog
        open={dialogOpen}
        title={editId ? 'Edit Location' : 'Add Location'}
        width="560px"
        submitLabel={editId ? 'Update' : 'Create'}
        isPending={saveMutation.isPending}
        onSubmit={(e) => {
          e.preventDefault();
          const found = validateForm();
          setErrors(found);
          if (Object.keys(found).length > 0) {
            if (found.name) document.getElementById('loc-name')?.focus();
            else if (found.phone) document.getElementById('loc-phone')?.focus();
            else if (found.furtherTaxRate) document.getElementById('loc-further-tax')?.focus();
            return;
          }
          saveMutation.mutate(buildPayload());
        }}
        onClose={() => { setDialogOpen(false); setEditId(null); setErrors({}); setForm(emptyForm); }}
      >
        <div className="space-y-4 p-1">
          <div>
            <Label htmlFor="loc-name">Name *</Label>
            <Input
              id="loc-name"
              value={form.name}
              maxLength={NAME_MAX}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'loc-name-error' : undefined}
              onChange={(e) => { setForm({ ...form, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: undefined }); }}
              placeholder="Main Store / Warehouse A"
            />
            {errors.name && <p id="loc-name-error" role="alert" className="mt-1 text-xs text-destructive-text">{errors.name}</p>}
          </div>
          <div>
            <Label>Type *</Label>
            <Dropdown
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.value })}
              options={LOCATION_TYPES.map((t) => ({ label: t.label, value: t.value }))}
              optionLabel="label"
              optionValue="value"
              className="w-full mt-1"
            />
          </div>
          <div>
            <Label htmlFor="loc-city">City</Label>
            <Input id="loc-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Karachi" />
          </div>
          <div>
            <Label htmlFor="loc-address">Address</Label>
            <Input id="loc-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street address" />
          </div>
          <div>
            <Label htmlFor="loc-phone">Phone</Label>
            <Input
              id="loc-phone"
              dir="ltr"
              value={form.phone}
              aria-invalid={!!errors.phone}
              aria-describedby={errors.phone ? 'loc-phone-error' : undefined}
              onChange={(e) => { setForm({ ...form, phone: e.target.value }); if (errors.phone) setErrors({ ...errors, phone: undefined }); }}
              placeholder="+92 300 0000000"
            />
            {errors.phone && <p id="loc-phone-error" role="alert" className="mt-1 text-xs text-destructive-text">{errors.phone}</p>}
          </div>
          {form.type === 'STORE' && (
          <Label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={form.isDefault}
              onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
            />
            Set as default location
          </Label>
          )}

          {/* ─── Tax Rules Section ───────────────────────────────── */}
          <div className="rounded-lg border">
            <button
              type="button"
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-start hover:bg-muted/30 transition-colors rounded-lg"
              onClick={() => setTaxSectionOpen((v) => !v)}
            >
              <span>Tax Rule Overrides</span>
              {taxSectionOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {taxSectionOpen && (
              <div className="px-4 pb-4 space-y-5 border-t pt-4">
                {/* Payment Method Tax */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Payment Method Tax</p>
                      <p className="text-xs text-muted-foreground">Override tax rate based on how customer pays (e.g. ICT restaurant rule)</p>
                    </div>
                    <Switch
                      aria-label="Payment Method Tax"
                      checked={form.paymentMethodTaxEnabled}
                      onCheckedChange={handleEnablePaymentTax}
                    />
                  </div>

                  {form.paymentMethodTaxEnabled && (
                    <div className="rounded-md border overflow-hidden text-xs">
                      <Table mobileCards>
                        <THead>
                          <Tr hover={false}>
                            <Th>{t('tblMethod')}</Th>
                            <Th>{t('tblLabel')}</Th>
                            <Th className="text-end w-20">{t('tblRatePct')}</Th>
                          </Tr>
                        </THead>
                        <TBody>
                          {PAYMENT_METHODS.map((method) => {
                            const rules = form.paymentMethodTaxRules ?? defaultPmRules();
                            const rule = rules[method] ?? { rate: 0, name: method, overridesProductTax: true };
                            return (
                              <Tr key={method}>
                                <Td label={t('tblMethod')} className="font-medium">{method}</Td>
                                <Td label={t('tblLabel')}>
                                  <Input
                                    className="h-8 px-2 py-1 text-xs"
                                    value={rule.name}
                                    onChange={(e) => updatePmRule(method, 'name', e.target.value)}
                                  />
                                </Td>
                                <Td label={t('tblRatePct')}>
                                  <Input
                                    type="number"
                                    min={0}
                                    max={100}
                                    step={0.5}
                                    className="h-8 px-2 py-1 text-xs text-end"
                                    value={rule.rate}
                                    onChange={(e) => updatePmRule(method, 'rate', e.target.value)}
                                  />
                                </Td>
                              </Tr>
                            );
                          })}
                        </TBody>
                      </Table>
                    </div>
                  )}
                </div>

                {/* ATL Further Tax — FBR addon required */}
                {canViewFbr && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">ATL Further Tax</p>
                      <p className="text-xs text-muted-foreground">Override ATL defaults and further tax rate for this branch</p>
                    </div>
                    <Switch
                      aria-label="ATL Further Tax"
                      checked={form.atlEnabled}
                      onCheckedChange={(on) => setForm((prev) => ({ ...prev, atlEnabled: on }))}
                    />
                  </div>

                  {form.atlEnabled && (
                    <div className="rounded-md border p-3 space-y-3">
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">Default buyer ATL status for walk-in sales at this branch</p>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setForm((prev) => ({ ...prev, defaultBuyerAtlStatus: false }))}
                            className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${!form.defaultBuyerAtlStatus ? 'bg-warning/10 border-warning/30 text-warning-text' : 'border-transparent hover:bg-muted/30 text-muted-foreground'}`}
                          >
                            <UserX className="h-3 w-3" /> Non-ATL (apply further tax)
                          </button>
                          <button
                            type="button"
                            onClick={() => setForm((prev) => ({ ...prev, defaultBuyerAtlStatus: true }))}
                            className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${form.defaultBuyerAtlStatus ? 'bg-success/10 border-success/30 text-success-text' : 'border-transparent hover:bg-muted/30 text-muted-foreground'}`}
                          >
                            <UserCheck className="h-3 w-3" /> ATL (no further tax)
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs" htmlFor="loc-further-tax">Further Tax Rate (%)</Label>
                        <Input
                          id="loc-further-tax"
                          type="number"
                          aria-invalid={!!errors.furtherTaxRate}
                          aria-describedby={errors.furtherTaxRate ? 'loc-further-tax-error' : undefined}
                          min={0}
                          max={100}
                          step={0.5}
                          className="mt-1"
                          value={form.furtherTaxRate}
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm((prev) => ({ ...prev, furtherTaxRate: v === '' ? '' : Number(v) }));
                            if (errors.furtherTaxRate) setErrors({ ...errors, furtherTaxRate: undefined });
                          }}
                        />
                        {errors.furtherTaxRate && <p id="loc-further-tax-error" role="alert" className="mt-1 text-xs text-destructive-text">{errors.furtherTaxRate}</p>}
                        <p className="text-xs text-muted-foreground mt-1">Standard rate under Sales Tax Act §3(1A) is 3%</p>
                      </div>
                    </div>
                  )}
                </div>
                )} {/* end canViewFbr ATL */}

                {/* FBR SRO — FBR addon required */}
                {canViewFbr && (
                <div className="space-y-1.5">
                  <Label>FBR SRO / Schedule No.</Label>
                  <Input
                    value={form.fbrSroScheduleNo}
                    onChange={(e) => setForm((prev) => ({ ...prev, fbrSroScheduleNo: e.target.value }))}
                    placeholder="e.g. ICT Tax on Services Ordinance 2001"
                  />
                  <p className="text-xs text-muted-foreground">
                    {form.paymentMethodTaxEnabled
                      ? 'Used as SRO for FBR submissions when payment method tax overrides are active (e.g. ICT restaurant 5%/15% rule)'
                      : 'Default SRO/Schedule applied to FBR submissions from this branch'}
                  </p>
                </div>
                )} {/* end canViewFbr FBR SRO */}
              </div>
            )}
          </div>
          {/* ─── End Tax Rules ───────────────────────────────────── */}
        </div>
      </FormDialog>

      {/* Stock Panel Dialog */}
      <Dialog
        header="Location Stock"
        visible={!!stockLocationId}
        onHide={() => { setStockLocationId(null); setStockPage(1); }}
        style={{ width: '640px' }}
        modal
      >
        {stockData ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground mb-3">
              Showing stock levels at <strong>{locations.find((l) => l.id === stockLocationId)?.name}</strong>{typeof stockData.total === 'number' && <> ({stockData.total} products)</>}
            </p>
            {(stockData.data ?? stockData).length === 0 && (stockData.total ?? 0) === 0 ? (
              <p className="text-muted-foreground text-sm">No stock recorded at this location yet.</p>
            ) : (
              <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('tblProduct')}</Th>
                    <Th>{t('tblSku')}</Th>
                    <Th className="text-end">{t('tblQty')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {(stockData.data ?? stockData).map((row: LocationStockRow) => (
                    <Tr key={row.id}>
                      <Td label={t('tblProduct')}>{row.product?.name ?? row.productId}</Td>
                      <Td label={t('tblSku')} className="text-muted-foreground">{row.product?.sku ?? '-'}</Td>
                      <Td label={t('tblQty')} className={`text-end font-medium ${row.quantity <= 0 ? 'text-destructive-text' : ''}`}>
                        {row.quantity}
                      </Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            )}
            {(stockData.total ?? 0) > STOCK_PAGE_SIZE && (
              <PaginationBar page={stockPage} pageSize={STOCK_PAGE_SIZE} total={stockData.total} onPageChange={setStockPage} />
            )}
          </div>
        ) : (
          <p className="text-muted-foreground">Loading stock...</p>
        )}
      </Dialog>

      {/* Deactivate Confirm Dialog */}
      <ConfirmDialog
        open={deactivateConfirm.open}
        title="Deactivate Location"
        message={`Deactivate "${deactivateConfirm.name}"? It will no longer appear as an active location.`}
        confirmLabel="Deactivate"
        variant="danger"
        loading={deactivateMutation.isPending}
        onConfirm={() => {
          deactivateMutation.mutate(deactivateConfirm.id);
          setDeactivateConfirm({ open: false, id: '', name: '' });
        }}
        onCancel={() => setDeactivateConfirm({ open: false, id: '', name: '' })}
      />
    </div>
  );
}
